import { useState } from 'react'
import type { FormEvent, ReactElement } from 'react'
import { Link } from 'react-router-dom'

import { errorMessage } from '../../lib/api'
import { requestPasswordReset } from '../../lib/auth'
import { useT, useTNode } from '../../lib/i18n'
import { Button } from '../../components/ui/Button'
import { Field } from '../../components/ui/Field'
import { Input } from '../../components/ui/Input'
import { Icon } from '../../components/ui/Icon'
import { InlineError } from '../../components/ui/ErrorState'
import { AuthLayout } from './AuthLayout'
import { emailProblem } from './validation'

const Forgot = (): ReactElement => {
  const t = useT()
  const tn = useTNode()
  const [email, setEmail] = useState('')
  const [fieldError, setFieldError] = useState<string | undefined>(undefined)
  const [formError, setFormError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [sent, setSent] = useState(false)

  const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault()
    const fault = emailProblem(email)
    setFieldError(fault === null ? undefined : t(fault))
    setFormError(null)
    if (fault !== null) return

    setSubmitting(true)
    try {
      await requestPasswordReset(email.trim())
      setSent(true)
    } catch (error) {
      setFormError(errorMessage(error))
    } finally {
      setSubmitting(false)
    }
  }

  if (sent) {
    return (
      <AuthLayout
        title={t('auth.checkInbox.title')}
        subtitle={tn('auth.forgot.sentSubtitle', {
          email: email.trim(),
          address: (chunk) => <span className="font-semibold text-ink">{chunk}</span>,
        })}
        footer={
          <p className="t-small text-ink-mute">
            <Link
              to="/login"
              className="ring-focus font-semibold text-brass-deep underline underline-offset-2"
            >
              {t('auth.common.backToSignIn')}
            </Link>
          </p>
        }
      >
        <div className="flex items-start gap-3 rounded-md border border-hair bg-surface p-4">
          <Icon name="info" size={18} className="mt-0.5 text-info" />
          <p className="t-small text-ink-mute">
            {tn('auth.forgot.resend', {
              again: (chunk) => (
                <button
                  type="button"
                  onClick={() => {
                    setSent(false)
                  }}
                  className="ring-focus font-semibold text-brass-deep underline underline-offset-2"
                >
                  {chunk}
                </button>
              ),
            })}
          </p>
        </div>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      title={t('auth.forgot.title')}
      subtitle={t('auth.forgot.subtitle')}
      footer={
        <p className="t-small text-ink-mute">
          {tn('auth.forgot.remembered', {
            signin: (chunk) => (
              <Link
                to="/login"
                className="ring-focus font-semibold text-brass-deep underline underline-offset-2"
              >
                {chunk}
              </Link>
            ),
          })}
        </p>
      }
    >
      <form
        noValidate
        onSubmit={(event) => {
          void onSubmit(event)
        }}
        className="flex flex-col gap-4"
      >
        {formError ? <InlineError>{formError}</InlineError> : null}

        <Field label={t('auth.fields.email')} error={fieldError} required>
          <Input
            type="email"
            name="email"
            value={email}
            autoComplete="email"
            autoFocus
            onChange={(event) => {
              setEmail(event.target.value)
            }}
          />
        </Field>

        <Button type="submit" variant="primary" size="lg" fullWidth loading={submitting}>
          {t('auth.forgot.submit')}
        </Button>
      </form>
    </AuthLayout>
  )
}

export default Forgot
